import { useState, useRef } from "react";
import { Button, useToast } from "@chakra-ui/react";
import { Upload, X, Loader2, Image as ImageIcon } from "lucide-react";
import React from "react";

export interface MediaItem {
  key: string;
  mimeType: string;
  originalUrl?: string;
  thumbnailUrl?: string;
}

interface MediaUploadProps {
  media: MediaItem[];
  onMediaChange: (media: MediaItem[]) => void;
  disabled?: boolean;
}

interface UploadResponse {
  success: boolean;
  images: Array<{
    key: string;
    mimeType: string;
    originalUrl: string;
    thumbnailUrl: string;
  }>;
}

export default function MediaUpload({
  media,
  onMediaChange,
  disabled = false,
}: MediaUploadProps) {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    // Validate file types
    const validTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    const invalidFiles = Array.from(files).filter(
      (file) => !validTypes.includes(file.type)
    );

    if (invalidFiles.length > 0) {
      toast({
        title: "Invalid file type",
        description: "Please select only JPEG, PNG, or WebP images.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    // Validate file sizes (5MB max per file)
    const maxSize = 5 * 1024 * 1024; // 5MB
    const oversizedFiles = Array.from(files).filter(
      (file) => file.size > maxSize
    );

    if (oversizedFiles.length > 0) {
      toast({
        title: "File too large",
        description: "Please select images smaller than 5MB.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      Array.from(files).forEach((file) => {
        formData.append("images", file);
      });

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/employer-job-application/upload-images`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Failed to upload images");
      }

      const data: UploadResponse = await response.json();

      if (data.success && data.images) {
        const newMediaItems: MediaItem[] = data.images.map((image) => {
          return {
            key: image.key,
            mimeType: image.mimeType,
            originalUrl: image.originalUrl,
            thumbnailUrl: image.thumbnailUrl,
          };
        });

        onMediaChange([...media, ...newMediaItems]);

        toast({
          title: "Image(s) uploaded",
          description: `Successfully uploaded ${data.images.length} image(s).`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "Upload failed",
        description: "Failed to upload images. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeMedia = (index: number) => {
    const updatedMedia = media.filter((_, i) => i !== index);
    onMediaChange(updatedMedia);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          isDisabled={disabled || uploading}
          className="flex items-center gap-2"
          leftIcon={
            uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )
          }
        >
          {uploading ? "Uploading..." : "Add images"}
        </Button>
        <span className="text-xs text-gray-500">
          JPEG, PNG, WebP (max 5MB each)
        </span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />

      {media.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {media.map((item, index) => (
            <div
              key={item.key}
              className="relative group rounded-lg overflow-hidden border bg-gray-50"
            >
              <div className="aspect-square relative">
                {item.mimeType.startsWith("image/") ? (
                  <img
                    src={item.thumbnailUrl || item.originalUrl}
                    alt={`Upload ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  </div>
                )}
                <Button
                  type="button"
                  colorScheme="red"
                  size="sm"
                  className="absolute top-1 right-1 h-6 w-6 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => removeMedia(index)}
                  isDisabled={disabled}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
